// frontend/src/components/PayPalConnectAccount.js
/**
 * PayPal Account Connection via Commerce Platform Partner Referral
 *
 * This component handles connecting a user's PayPal account to Gifted
 * using PayPal's Partner Referral (Commerce Platform) onboarding.
 *
 * This grants Gifted permission to:
 * - Accept payments on behalf of the user (PAYMENT)
 * - Issue refunds (REFUND)
 * - Collect platform fees (PARTNER_FEE)
 * - Hold funds with delayed disbursement (DELAY_FUNDS_DISBURSEMENT)
 *
 * Works with personal PayPal accounts (Before Payment onboarding).
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SERVER_URL } from '../config';

const TRACKING_ID_KEY = 'paypal_tracking_id';

const PayPalConnectAccount = ({ onSuccess, onStatusChange }) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, not_connected, connecting, completing, connected, issues, error
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState(null);
  const [statusDetails, setStatusDetails] = useState(null); // Detailed status from PayPal
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const popupRef = useRef(null);
  const popupCheckRef = useRef(null);
  const focusListenerRef = useRef(null);

  // Listen for postMessage from the PayPal popup when PayPal's redirect
  // lands on our /settings?paypal_onboarding=complete URL inside the popup.
  // This is the happy path — the popup component branch below sends this
  // and we complete the connection here in the parent window.
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'paypal_onboarding_complete' && event.data?.merchantId) {
        if (popupCheckRef.current) {
          clearInterval(popupCheckRef.current);
          popupCheckRef.current = null;
        }
        sessionStorage.removeItem(TRACKING_ID_KEY);
        completeOnboarding(event.data.merchantId, {
          permissionsGranted: event.data.permissionsGranted,
          isEmailConfirmed: event.data.isEmailConfirmed,
          accountStatus: event.data.accountStatus,
          consentStatus: event.data.consentStatus,
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (popupCheckRef.current) {
        clearInterval(popupCheckRef.current);
        popupCheckRef.current = null;
      }
      if (focusListenerRef.current) {
        window.removeEventListener('focus', focusListenerRef.current);
        focusListenerRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Check if returning from PayPal onboarding
    const onboardingStatus = searchParams.get('paypal_onboarding');
    const merchantId = searchParams.get('merchantIdInPayPal');

    if (onboardingStatus === 'complete') {
      if (window.opener && merchantId) {
        // Component is rendering inside the popup — notify the parent
        // window via postMessage and close ourselves. Parent will handle
        // the actual completion in the message handler above.
        window.opener.postMessage({
          type: 'paypal_onboarding_complete',
          merchantId,
          permissionsGranted: searchParams.get('permissionsGranted') === 'true',
          isEmailConfirmed: searchParams.get('isEmailConfirmed') === 'true',
          accountStatus: searchParams.get('accountStatus'),
          consentStatus: searchParams.get('consentStatus') === 'true',
        }, window.location.origin);
        window.close();
        return;
      }

      if (merchantId) {
        // Full-page redirect path (popup was blocked, so we navigated the
        // whole tab to PayPal and are now back).
        completeOnboarding(merchantId, {
          permissionsGranted: searchParams.get('permissionsGranted') === 'true',
          isEmailConfirmed: searchParams.get('isEmailConfirmed') === 'true',
          accountStatus: searchParams.get('accountStatus'),
          consentStatus: searchParams.get('consentStatus') === 'true',
        });
      }
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // If we have a tracking_id from a previous Connect attempt, the user
    // may have completed PayPal onboarding without the auto-redirect firing
    // (e.g. sandbox casualSellerDone, or PayPal's "Return to Gifted"
    // button being unresponsive). Try the tracking_id lookup transparently.
    if (sessionStorage.getItem(TRACKING_ID_KEY)) {
      recoverByTrackingId();
    } else {
      checkConnectionStatus();
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkConnectionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${SERVER_URL}/paypal/onboard/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.connected && data.payments_receivable && data.primary_email_confirmed) {
          setStatus('connected');
          setPaypalEmail(data.paypal_email || (data.merchant_id ? `Account ${data.merchant_id}` : null));
          setStatusDetails(data);
          if (onStatusChange) onStatusChange('connected');
        } else if (data.connected) {
          // Connected but has issues (payments not receivable or email not confirmed)
          setPaypalEmail(data.paypal_email || (data.merchant_id ? `Account ${data.merchant_id}` : null));
          setStatusDetails(data);
          setStatus('issues');
          if (onStatusChange) onStatusChange('issues');
        } else {
          setStatus('not_connected');
          if (onStatusChange) onStatusChange('not_connected');
        }
      } else {
        setStatus('not_connected');
      }
    } catch (err) {
      console.error('Error checking PayPal status:', err);
      setStatus('not_connected');
    }
  };

  const refreshStatus = async () => {
    setIsRefreshing(true);
    if (sessionStorage.getItem(TRACKING_ID_KEY)) {
      await recoverByTrackingId();
    } else {
      await checkConnectionStatus();
    }
    setIsRefreshing(false);
  };

  // Look up the in-flight onboarding by tracking_id. Used when the user
  // returns to Settings without PayPal having redirected back with merchantId
  // (sandbox casualSellerDone, or a broken "Return to Gifted" button).
  const recoverByTrackingId = async () => {
    const tid = sessionStorage.getItem(TRACKING_ID_KEY);
    if (!tid) {
      return checkConnectionStatus();
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${SERVER_URL}/paypal/onboard/lookup?tracking_id=${encodeURIComponent(tid)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();

      if (data.found) {
        sessionStorage.removeItem(TRACKING_ID_KEY);
        setStatus('connected');
        if (onSuccess) onSuccess(data.merchant_id);
        if (onStatusChange) onStatusChange('connected');
        // Refresh full status details (email confirmed, payments receivable etc.)
        await checkConnectionStatus();
      } else {
        // Lookup didn't find them yet — webhook may not have fired.
        // Leave the tracking_id in place so a manual Refresh can retry,
        // and fall through to the regular status check.
        await checkConnectionStatus();
      }
    } catch (err) {
      console.error('Tracking-ID lookup failed:', err);
      await checkConnectionStatus();
    }
  };

  const completeOnboarding = async (merchantId, params = {}) => {
    setStatus('completing');

    const { permissionsGranted = false, isEmailConfirmed = false, accountStatus = null, consentStatus = false } = params;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${SERVER_URL}/paypal/onboard/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          merchant_id: merchantId,
          permissions_granted: permissionsGranted,
          is_email_confirmed: isEmailConfirmed,
          account_status: accountStatus,
          consent_status: consentStatus,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('connected');
        setConfirmDisconnect(false);
        if (onSuccess) onSuccess(merchantId);
        if (onStatusChange) onStatusChange('connected');
      } else {
        setStatus('error');
        setError(data.message || 'Failed to complete PayPal setup.');
        if (data.debug) {
          setDebugInfo(data.debug);
        }
      }
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setStatus('error');
      setError('Failed to verify PayPal account. Please try again.');
    }
  };

  const handleCancelConnect = () => {
    if (popupCheckRef.current) {
      clearInterval(popupCheckRef.current);
      popupCheckRef.current = null;
    }
    if (focusListenerRef.current) {
      window.removeEventListener('focus', focusListenerRef.current);
      focusListenerRef.current = null;
    }
    try { if (popupRef.current && !popupRef.current.closed) popupRef.current.close(); } catch (_) {}
    popupRef.current = null;
    sessionStorage.removeItem(TRACKING_ID_KEY);
    setStatus('not_connected');
  };

  const handleConnect = async () => {
    setIsGeneratingLink(true);
    setError(null);

    // Open a new tab SYNCHRONOUSLY inside the click handler. We point it at
    // about:blank first, then navigate it to PayPal once the backend returns
    // the URL. Opening it after the fetch resolves would lose the user-gesture
    // context and the popup blocker would block it.
    //
    // No size features (width/height/etc) — those make browsers treat the
    // window as a "popup", which is blocked by default in Chrome and others.
    // With no features, it opens as a regular new tab.
    const popup = window.open('about:blank', 'PayPalSignup');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${SERVER_URL}/paypal/onboard`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (popup) try { popup.close(); } catch (_) {}
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start PayPal onboarding');
      }

      const data = await response.json();
      if (!data.action_url) {
        if (popup) try { popup.close(); } catch (_) {}
        throw new Error('No onboarding URL returned');
      }

      // Remember the tracking_id so we can recover the connection if PayPal
      // doesn't redirect back with merchantIdInPayPal (sandbox casualSellerDone,
      // or a broken "Return to Gifted" button).
      if (data.tracking_id) {
        sessionStorage.setItem(TRACKING_ID_KEY, data.tracking_id);
      }

      if (!popup) {
        // Tab was blocked even when opened synchronously — fall back to a
        // full-page redirect. The sessionStorage tracking_id will let us
        // recover on return.
        window.location.href = data.action_url;
        return;
      }

      // Navigate the already-opened tab to PayPal.
      popup.location.href = data.action_url;
      popupRef.current = popup;
      setIsGeneratingLink(false);
      setStatus('connecting');

      // Watch for the user to come back. Two trigger paths:
      //   1. The PayPal tab closes — either because PayPal's "Return to Gifted"
      //      redirected it through our in-popup postMessage branch (which calls
      //      window.close()), or the user closed the tab manually.
      //   2. The user clicks back on this tab without closing the PayPal one
      //      (focus event fires).
      // Either way, run the tracking_id recovery — if the connection isn't
      // ready yet, the lookup will report not_found and we'll fall through
      // to a normal status check.
      // Guard with `recoveryStarted` because both triggers can fire for one
      // close (close interval + focus event), and the focus path is deferred
      // 200ms below so the first call's clears don't preempt it.
      let recoveryStarted = false;
      const triggerRecovery = () => {
        if (recoveryStarted) return;
        recoveryStarted = true;
        if (popupCheckRef.current) {
          clearInterval(popupCheckRef.current);
          popupCheckRef.current = null;
        }
        if (focusListenerRef.current) {
          window.removeEventListener('focus', focusListenerRef.current);
          focusListenerRef.current = null;
        }
        try { if (popup && !popup.closed) popup.close(); } catch (_) {}
        popupRef.current = null;
        setStatus('completing');
        recoverByTrackingId();
      };

      const onFocus = () => {
        // Defer slightly so the postMessage handler (if firing) lands first.
        setTimeout(triggerRecovery, 200);
      };

      popupCheckRef.current = setInterval(() => {
        if (popup.closed) triggerRecovery();
      }, 500);
      focusListenerRef.current = onFocus;
      window.addEventListener('focus', onFocus);
    } catch (err) {
      console.error('Error starting PayPal onboarding:', err);
      setError(err.message);
      setIsGeneratingLink(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${SERVER_URL}/paypal/disconnect`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        sessionStorage.removeItem(TRACKING_ID_KEY);
        setStatus('not_connected');
        setConfirmDisconnect(false);
        if (onStatusChange) onStatusChange('not_connected');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Error disconnecting PayPal:', err);
      setError('Failed to disconnect PayPal account');
    }
  };

  if (status === 'loading') {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
          <span className="text-sm text-gray-600">Checking PayPal status...</span>
        </div>
      </div>
    );
  }

  if (status === 'connecting') {
    return (
      <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-3">
        <div className="flex items-start gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full flex-shrink-0 mt-0.5"></div>
          <div className="text-sm text-indigo-700">
            <p className="font-medium mb-1">PayPal signup in progress</p>
            <p>Finish signing up in the PayPal tab that just opened. When you're done, close it (or come back to this tab) and we'll finish setting things up here.</p>
          </div>
        </div>
        <button
          onClick={handleCancelConnect}
          className="w-full px-4 py-2 text-sm text-indigo-600 hover:text-indigo-800 underline"
        >
          Cancel
        </button>
      </div>
    );
  }

  if (status === 'completing') {
    return (
      <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
          <span className="text-sm text-indigo-700">Verifying your PayPal account...</span>
        </div>
      </div>
    );
  }

  const disconnectButton = confirmDisconnect ? (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-gray-600">Disconnect PayPal?</span>
      <button onClick={handleDisconnect} className="text-red-600 hover:text-red-800 underline">Yes, disconnect</button>
      <button onClick={() => setConfirmDisconnect(false)} className="text-gray-500 hover:text-gray-700 underline">Cancel</button>
    </div>
  ) : (
    <button onClick={() => setConfirmDisconnect(true)} className="text-xs text-gray-500 hover:text-gray-700 underline self-center">
      Disconnect
    </button>
  );

  if (status === 'connected') {
    return (
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium text-green-800">PayPal Connected</span>
            </div>
            <p className="text-sm text-green-700">
              Your PayPal account is connected and ready to receive gifts.
            </p>
            {statusDetails?.paypal_email && (
              <p className="text-sm text-green-600 mt-1">
                {statusDetails.paypal_email}
              </p>
            )}
          </div>
          {disconnectButton}
        </div>
      </div>
    );
  }

  if (status === 'issues') {
    const emailNotConfirmed = statusDetails && !statusDetails.primary_email_confirmed;
    const paymentsNotReceivable = statusDetails && !statusDetails.payments_receivable;
    const oauthMissing = statusDetails && Array.isArray(statusDetails.oauth_integrations) && statusDetails.oauth_integrations.length === 0;

    return (
      <div className="space-y-3">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="font-medium text-yellow-800">PayPal Account Needs Attention</span>
          </div>

          {paypalEmail && (
            <p className="text-sm text-yellow-700 mb-3">
              {paypalEmail}
            </p>
          )}

          <div className="space-y-3">
            {emailNotConfirmed && (
              <div className="p-3 bg-white border border-yellow-300 rounded-md">
                <p className="text-sm font-medium text-yellow-900 mb-1">Email Not Confirmed</p>
                <p className="text-sm text-yellow-800">
                  Please confirm your email address on{' '}
                  <a href="https://www.paypal.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                    paypal.com
                  </a>{' '}
                  in order to receive payments. You currently cannot receive payments.
                  Once done, simply revisit this page to refresh the onboarding status.
                </p>
              </div>
            )}

            {paymentsNotReceivable && !emailNotConfirmed && (
              <div className="p-3 bg-white border border-yellow-300 rounded-md">
                <p className="text-sm font-medium text-yellow-900 mb-1">Cannot Receive Payments</p>
                <p className="text-sm text-yellow-800">
                  You currently cannot receive payments due to a possible restriction on your PayPal account.
                  Please reach out to{' '}
                  <a href="https://www.paypal.com/smarthelp/contact-us" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                    PayPal Customer Support
                  </a>{' '}
                  or connect to{' '}
                  <a href="https://www.paypal.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                    paypal.com
                  </a>{' '}
                  for more information. Once sorted, simply revisit this page to refresh the onboarding status.
                </p>
              </div>
            )}

            {oauthMissing && (
              <div className="p-3 bg-white border border-yellow-300 rounded-md">
                <p className="text-sm font-medium text-yellow-900 mb-1">Permissions Not Granted</p>
                <p className="text-sm text-yellow-800">
                  There is an issue with your onboarding with PayPal. Please go through the
                  onboarding flow again and grant third-party permissions to Gifted.
                </p>
                <button
                  onClick={handleConnect}
                  disabled={isGeneratingLink}
                  className="mt-2 px-4 py-2 bg-[#0070ba] text-white rounded-lg hover:bg-[#005ea6] transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {isGeneratingLink ? 'Preparing...' : 'Reconnect PayPal'}
                </button>
              </div>
            )}
          </div>

          <div className="mt-4 flex gap-3">
            <button
              onClick={refreshStatus}
              disabled={isRefreshing}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {isRefreshing ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Checking...
                </>
              ) : (
                'Refresh Status'
              )}
            </button>
            {disconnectButton}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium text-red-800">Connection Failed</span>
          </div>
          <p className="text-sm text-red-700 mb-3">{error}</p>
          {debugInfo && debugInfo.hints && (
            <ul className="text-xs text-red-600 mb-3 list-disc list-inside">
              {debugInfo.hints.map((hint, i) => (
                <li key={i}>{hint}</li>
              ))}
            </ul>
          )}
          <button
            onClick={() => {
              setError(null);
              setDebugInfo(null);
              setStatus('not_connected');
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Not connected state
  return (
    <div className="space-y-4">
      <div className="p-4 bg-indigo-50 border-2 border-indigo-300 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-medium text-indigo-900">Connect Your PayPal Account</h3>
          <span className="px-2 py-0.5 bg-indigo-500 text-white text-xs font-medium rounded-full">
            Required
          </span>
        </div>
        <p className="text-sm text-indigo-700 mb-3">
          Connect your PayPal account to receive gifts. Works with personal or business accounts.
        </p>

        <div className="space-y-2 text-sm text-indigo-600 mb-4">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Receive gifts directly to your PayPal</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Works with personal PayPal accounts</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Secure - funds held by PayPal, not Gifted</span>
          </div>
        </div>

        <button
          onClick={handleConnect}
          disabled={isGeneratingLink}
          className="w-full px-4 py-3 bg-[#0070ba] text-white rounded-lg hover:bg-[#005ea6] transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isGeneratingLink ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              Preparing...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.644h6.024c2.637 0 4.475.521 5.466 1.549.93.965 1.277 2.396.997 4.131l-.016.097v.081c-.008.044-.016.088-.024.132l-.016.08c-.562 3.066-2.489 4.631-5.732 4.655h-1.537a.771.771 0 0 0-.757.644l-.943 5.892a.641.641 0 0 1-.633.545H7.076z"/>
              </svg>
              Connect with PayPal
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
};

export default PayPalConnectAccount;
